import React from 'react';
import { Box, Button, Container, Typography } from '@mui/material';
import { useTranslation } from '../contexts/TranslationContext';
import { getLocalizedString, type LocalizedString } from '../utils/localizedText';

export type PageHeroSectionConfig = {
  enabled?: boolean;
  title?: LocalizedString;
  subtitle?: LocalizedString;
  ctaText?: LocalizedString;
  ctaUrl?: string;
  backgroundImage?: string;
  align?: 'left' | 'center';
  minHeight?: number;
};

type Props = {
  config?: PageHeroSectionConfig | null;
  resolvePublicUrl?: (p?: string) => string;
};

const PageHeroSection: React.FC<Props> = ({ config, resolvePublicUrl }) => {
  const cfg: PageHeroSectionConfig = (config && typeof config === 'object') ? config : {};
  const { language } = useTranslation();
  if (cfg.enabled === false) return null;

  const title = getLocalizedString(cfg.title, language, 'en', 'Dein Marktplatz für besondere Funde');
  const subtitle = getLocalizedString(cfg.subtitle, language, 'en', 'Entdecke großartige Produkte von Top-Marken und unabhängigen Verkäufern.');
  const ctaText = getLocalizedString(cfg.ctaText, language, 'en', '');
  const ctaUrl = typeof cfg.ctaUrl === 'string' ? cfg.ctaUrl : '';
  const align: 'left' | 'center' = cfg.align === 'left' ? 'left' : 'center';
  const minHeight = Math.max(360, Math.min(900, Number(cfg.minHeight) || 520));

  const bg = typeof cfg.backgroundImage === 'string' ? cfg.backgroundImage : '';
  const bgUrl = bg ? (resolvePublicUrl ? resolvePublicUrl(bg) : bg) : '';

  return (
    <Box
      sx={{
        position: 'relative',
        minHeight,
        color: 'common.white',
        display: 'flex',
        alignItems: 'center',
        overflow: 'hidden',
        bgcolor: 'grey.900',
      }}
    >
      {bgUrl ? (
        <Box
          component="img"
          src={bgUrl}
          alt="Hero background"
          sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', filter: 'brightness(0.85)' }}
        />
      ) : null}

      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          background:
            'radial-gradient(900px 420px at 10% 20%, rgba(59,130,246,0.35), transparent), linear-gradient(180deg, rgba(0,0,0,0.25), rgba(0,0,0,0.65))',
        }}
      />

      <Container maxWidth="lg" sx={{ position: 'relative', py: { xs: 7, md: 10 } }}>
        <Box sx={{ maxWidth: 720, textAlign: align, mx: align === 'center' ? 'auto' : 0 }}>
          <Typography variant="h2" sx={{ fontWeight: 950, lineHeight: 1.05, textShadow: '0 8px 30px rgba(0,0,0,0.35)' }}>
            {title}
          </Typography>
          <Typography variant="h6" sx={{ mt: 2, color: 'rgba(255,255,255,0.9)', textShadow: '0 8px 30px rgba(0,0,0,0.35)' }}>
            {subtitle}
          </Typography>

          {ctaText && ctaUrl ? (
            <Box sx={{ mt: 3 }}>
              <Button
                variant="contained"
                href={ctaUrl}
                sx={{
                  borderRadius: 999,
                  px: 3,
                  py: 1.25,
                  fontWeight: 900,
                  boxShadow: '0 16px 50px rgba(59,130,246,0.35)',
                }}
              >
                {ctaText}
              </Button>
            </Box>
          ) : null}
        </Box>
      </Container>
    </Box>
  );
};

export default PageHeroSection;
